import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useAtomValue } from 'jotai';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  WS_EVENTS,
  type AuthSuccessPayload,
  type CallAcceptPayload,
  type CallConfig,
  type CallEndPayload,
  type CallErrorPayload,
  type CallInvitePayload,
  type CallMediaPayload,
  type CallRingingPayload,
  type CallMediaState,
  type CallParticipant,
  type CallParticipantPayload,
  type CallRejectPayload,
  type CallSignalIntent,
  type CallSignalPayload,
  type CallType,
} from '@twinden/shared';
import { currentUserAtom } from '../store';
import { api } from '../lib/api';
import { countClientFailure } from '../lib/errors';
import { useWebSocket } from './useWebSocket';
import { t } from '../i18n';
import { afterReconnect, withoutCallParam } from '../lib/notificationLinks';
import {
  INITIAL_VIDEO_FALLBACK,
  VIDEO_STATS_INTERVAL_MS,
  aloneAfterLeaving,
  readVideoSenderStats,
  senderBitrateCap,
  senderScaleDown,
  startingVideoCapture,
  videoFallbackDecision,
  type NetworkInformationLike,
  type RtcStat,
  type VideoConnectionHistory,
  type VideoFallbackMode,
  type VideoFallbackState,
  type VideoLinkSample,
} from '../lib/callRules';

// Mesh WebRTC client (M19). Every accepted participant connects to every other;
// the server relays SDP/ICE and never touches media.

export type CallStatus = 'idle' | 'ringing' | 'connecting' | 'active';

export interface CallUiState {
  status: CallStatus;
  callId: string | null;
  conversationId: string | null;
  type: CallType;
  direction: 'outgoing' | 'incoming';
  initiator: CallParticipant | null;
  /** Accepted participant ids, including the local user. */
  participants: string[];
  localStream: MediaStream | null;
  remoteStreams: Record<string, MediaStream>;
  micEnabled: boolean;
  cameraEnabled: boolean;
  /** Other people's mic/camera state, as they last reported it (M24, D36). */
  remoteMedia: Record<string, CallMediaState>;
  /** People whose connection dropped and may come back (D37). */
  reconnecting: string[];
  /** Which way the camera faces; only the front one is mirrored (D39). */
  facingMode: 'user' | 'environment';
  canFlipCamera: boolean;
  /**
   * Weak connection (D225): `paused` when we turned the camera off,
   * `better` when it has recovered and we suggest turning it back on.
   */
  videoFallback: 'paused' | 'better' | null;
  /**
   * Our call: the people whose phone has said it's ringing (D278). Empty is
   * "Calling…", anyone is "Ringing…" with the ringback.
   */
  ringing: string[];
}

const IDLE_STATE: CallUiState = {
  status: 'idle',
  callId: null,
  conversationId: null,
  type: 'voice',
  direction: 'outgoing',
  initiator: null,
  participants: [],
  localStream: null,
  remoteStreams: {},
  micEnabled: true,
  cameraEnabled: false,
  remoteMedia: {},
  reconnecting: [],
  facingMode: 'user',
  canFlipCamera: false,
  videoFallback: null,
  ringing: [],
};

interface CallApi {
  state: CallUiState;
  notice: string | null;
  error: string | null;
  startCall: (conversationId: string, type: CallType) => Promise<void>;
  acceptCall: () => Promise<void>;
  rejectCall: () => void;
  endCall: () => void;
  toggleMic: () => void;
  toggleCamera: () => void;
  /** Ring more people from this chat (D38). */
  ringMore: (userIds: string[]) => void;
  flipCamera: () => Promise<void>;
  /** "Turn video on" on the weak-connection chip; hands off after (D225). */
  resumeVideo: () => void;
  /** Close "Connection better — turn video on?" and keep video off (D225). */
  dismissVideoHint: () => void;
  dismissNotice: () => void;
}

const CONFIG_MAX_AGE_MS = 60 * 60 * 1000;

const CallContext = createContext<CallApi | null>(null);

type CallSignal =
  | { type: 'offer'; sdp: RTCSessionDescriptionInit }
  | { type: 'answer'; sdp: RTCSessionDescriptionInit }
  | { type: 'candidate'; candidate: RTCIceCandidateInit };

interface Session {
  callId: string;
  conversationId: string;
  type: CallType;
  direction: 'outgoing' | 'incoming';
  initiator: CallParticipant;
  /** The server has announced our own accept (issue #21). */
  confirmed?: boolean;
}

function mediaErrorMessage(err: unknown): string {
  const name = (err as { name?: string } | null)?.name;
  if (name === 'NotAllowedError') {
    return t('chat.callErrors.denied');
  }
  if (name === 'NotFoundError') {
    return t('chat.callErrors.notFound');
  }
  if (name === 'NotReadableError') {
    return t('chat.callErrors.inUse');
  }
  return err instanceof Error
    ? err.message
    : t('chat.callErrors.couldNotStart');
}

// Chrome and Android have it; Safari and Firefox don't (treated as Wi-Fi, D226).
function networkConnection(): NetworkInformationLike | null {
  return (
    (navigator as Navigator & { connection?: NetworkInformationLike })
      .connection ?? null
  );
}

// 360p on mobile data, 720p otherwise (D226).
function videoConstraints(
  facingMode: ConstrainDOMString
): MediaTrackConstraints {
  const capture = startingVideoCapture(networkConnection());
  return {
    width: { ideal: capture.width },
    height: { ideal: capture.height },
    ...(capture.frameRate ? { frameRate: { ideal: capture.frameRate } } : {}),
    facingMode,
  };
}

async function acquireMedia(type: CallType): Promise<MediaStream> {
  return navigator.mediaDevices.getUserMedia({
    audio: true,
    video: type === 'video' ? videoConstraints('user') : false,
  });
}

interface SenderEncoding {
  maxBitrate: number;
  scaleResolutionDownBy: number;
}

// One setParameters at a time per sender: a second call while one is in
// flight is refused by the browser, so passes queue behind each other.
const encodingQueue = new WeakMap<RTCRtpSender, Promise<void>>();

// Cap one outgoing video copy (D226, D225). Encodings only exist once the
// sender is negotiated; a refused pass is retried on the next stats tick.
function setSenderEncoding(sender: RTCRtpSender, want: SenderEncoding) {
  const previous = encodingQueue.get(sender) ?? Promise.resolve();
  const next = previous.then(async () => {
    try {
      const params = sender.getParameters();
      const encoding = params.encodings?.[0];
      if (!encoding) return;
      if (
        encoding.maxBitrate === want.maxBitrate &&
        (encoding.scaleResolutionDownBy ?? 1) === want.scaleResolutionDownBy
      ) {
        return;
      }
      encoding.maxBitrate = want.maxBitrate;
      encoding.scaleResolutionDownBy = want.scaleResolutionDownBy;
      await sender.setParameters(params);
    } catch {
      // Not negotiated yet, or the browser refused: the next pass retries.
    }
  });
  encodingQueue.set(sender, next);
}

// What D225 keeps per connection. Keyed by the connection itself, so a peer
// rebuilt after a rejoin starts over (warm-up included).
interface ConnectionWatch {
  connectedAt: number | null;
  samples: VideoLinkSample[];
  previous: { bytesSent: number | null; timestamp: number | null } | null;
}
const watchByPc = new WeakMap<RTCPeerConnection, ConnectionWatch>();

const videoSender = (pc: RTCPeerConnection) =>
  pc.getSenders().find((sender) => sender.track?.kind === 'video');

const fallbackBanner = (
  mode: VideoFallbackMode
): CallUiState['videoFallback'] =>
  mode === 'paused' ? 'paused' : mode === 'better' ? 'better' : null;

// Samples older than this can't matter to D225's 8 s and 20 s runs.
const VIDEO_SAMPLE_WINDOW_MS = 30_000;

// More than one camera means front and back (D39). Labels and counts are only
// reliable once camera permission is granted, so ask after the call starts.
async function hasSeveralCameras(): Promise<boolean> {
  try {
    const devices = await navigator.mediaDevices.enumerateDevices();
    return devices.filter((device) => device.kind === 'videoinput').length > 1;
  } catch {
    return false;
  }
}

const withoutId = (list: string[], id: string) =>
  list.filter((entry) => entry !== id);

function CallProviderInner({ children }: { children: ReactNode }) {
  const { sendMessage, onEvent } = useWebSocket();
  const currentUser = useAtomValue(currentUserAtom);
  const currentUserRef = useRef(currentUser);
  currentUserRef.current = currentUser;

  const [state, setState] = useState<CallUiState>(IDLE_STATE);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const configRef = useRef<CallConfig | null>(null);
  const sessionRef = useRef<Session | null>(null);
  const localStreamRef = useRef<MediaStream | null>(null);
  const peersRef = useRef<Map<string, RTCPeerConnection>>(new Map());
  const pendingCandidatesRef = useRef<Map<string, RTCIceCandidateInit[]>>(
    new Map()
  );
  // Weak-connection watch (D225): one per call.
  const fallbackRef = useRef<VideoFallbackState>(INITIAL_VIDEO_FALLBACK);

  // TURN credentials expire (24 h with Cloudflare), so an app left open
  // fetches fresh call config at most an hour after the last one.
  const configAtRef = useRef(0);
  const loadConfig = useCallback(async (): Promise<CallConfig | null> => {
    if (
      configRef.current &&
      Date.now() - configAtRef.current < CONFIG_MAX_AGE_MS
    )
      return configRef.current;
    try {
      const response = await api.getCallConfig();
      if (response.success) {
        configRef.current = response.data;
        configAtRef.current = Date.now();
      }
      return response.data;
    } catch {
      return null;
    }
  }, []);

  const sendSignal = useCallback(
    (targetUserId: string, signal: CallSignal) => {
      const session = sessionRef.current;
      if (!session) return;
      sendMessage(WS_EVENTS.CALL_SIGNAL, {
        callId: session.callId,
        targetUserId,
        signal,
      } satisfies CallSignalIntent);
    },
    [sendMessage]
  );

  // Tell the others whether my mic and camera are on (D36).
  const announceMedia = useCallback(
    (micEnabled: boolean, cameraEnabled: boolean) => {
      const session = sessionRef.current;
      if (!session) return;
      sendMessage(WS_EVENTS.CALL_MEDIA, {
        callId: session.callId,
        micEnabled,
        cameraEnabled,
      });
    },
    [sendMessage]
  );
  const mediaRef = useRef({ micEnabled: true, cameraEnabled: false });
  mediaRef.current = {
    micEnabled: state.micEnabled,
    cameraEnabled: state.cameraEnabled,
  };

  const teardown = useCallback((message: string | null = null) => {
    for (const pc of peersRef.current.values()) {
      pc.onicecandidate = null;
      pc.ontrack = null;
      pc.onconnectionstatechange = null;
      pc.close();
    }
    peersRef.current.clear();
    pendingCandidatesRef.current.clear();
    fallbackRef.current = INITIAL_VIDEO_FALLBACK;
    localStreamRef.current?.getTracks().forEach((track) => track.stop());
    localStreamRef.current = null;
    sessionRef.current = null;
    setState(IDLE_STATE);
    setNotice(message);
  }, []);

  // Drop the connection to one peer so the next offer builds a fresh one. Used
  // when that peer rejoins after a reconnect; its old transport can't be reused.
  const closePeer = useCallback((peerId: string) => {
    const pc = peersRef.current.get(peerId);
    if (pc) {
      pc.onicecandidate = null;
      pc.ontrack = null;
      pc.onconnectionstatechange = null;
      pc.close();
      peersRef.current.delete(peerId);
    }
    pendingCandidatesRef.current.delete(peerId);
    setState((prev) => {
      if (!(peerId in prev.remoteStreams)) return prev;
      const remoteStreams = { ...prev.remoteStreams };
      delete remoteStreams[peerId];
      return { ...prev, remoteStreams };
    });
  }, []);

  // Mesh: one upload per other person, so the cap depends on call size
  // (D226); a weak minority's copies are capped lower still (D225).
  const senderCap = useCallback((peerId: string): number => {
    const base = senderBitrateCap(
      networkConnection(),
      peersRef.current.size + 1
    );
    const lowered = fallbackRef.current.lowered[peerId];
    return lowered === undefined ? base : Math.min(base, lowered);
  }, []);

  const applyBitrateCaps = useCallback(() => {
    const scaleResolutionDownBy = senderScaleDown(
      networkConnection(),
      peersRef.current.size + 1
    );
    for (const [peerId, pc] of peersRef.current) {
      const sender = videoSender(pc);
      if (!sender) continue;
      setSenderEncoding(sender, {
        maxBitrate: senderCap(peerId),
        scaleResolutionDownBy,
      });
    }
  }, [senderCap]);

  const ensurePeer = useCallback(
    (peerId: string): RTCPeerConnection => {
      const existing = peersRef.current.get(peerId);
      if (existing) return existing;

      const pc = new RTCPeerConnection({
        iceServers: (configRef.current?.iceServers ?? []) as RTCIceServer[],
      });

      const stream = localStreamRef.current;
      if (stream) {
        for (const track of stream.getTracks()) pc.addTrack(track, stream);
      }

      pc.onicecandidate = (event) => {
        if (event.candidate) {
          sendSignal(peerId, {
            type: 'candidate',
            candidate: event.candidate.toJSON(),
          });
        }
      };

      pc.ontrack = (event) => {
        const [remoteStream] = event.streams;
        if (!remoteStream) return;
        setState((prev) => ({
          ...prev,
          remoteStreams: { ...prev.remoteStreams, [peerId]: remoteStream },
        }));
      };

      pc.onconnectionstatechange = () => {
        if (pc.connectionState === 'connected') {
          // (Re)connected: a fresh warm-up and run of samples (D225).
          watchByPc.set(pc, {
            connectedAt: Date.now(),
            samples: [],
            previous: null,
          });
          applyBitrateCaps();
          setState((prev) => ({
            ...prev,
            status: 'active',
            reconnecting: withoutId(prev.reconnecting, peerId),
          }));
        }
        if (pc.connectionState === 'disconnected') {
          setState((prev) =>
            prev.reconnecting.includes(peerId)
              ? prev
              : { ...prev, reconnecting: [...prev.reconnecting, peerId] }
          );
        }
        if (
          pc.connectionState === 'failed' ||
          pc.connectionState === 'closed'
        ) {
          pc.close();
          peersRef.current.delete(peerId);
          setState((prev) => {
            const remoteStreams = { ...prev.remoteStreams };
            delete remoteStreams[peerId];
            return { ...prev, remoteStreams };
          });
        }
      };

      peersRef.current.set(peerId, pc);
      return pc;
    },
    [applyBitrateCaps, sendSignal]
  );

  const flushCandidates = useCallback(
    async (peerId: string, pc: RTCPeerConnection) => {
      const pending = pendingCandidatesRef.current.get(peerId);
      if (!pending || pending.length === 0) return;
      pendingCandidatesRef.current.delete(peerId);
      for (const candidate of pending) {
        await pc.addIceCandidate(candidate).catch(() => undefined);
      }
    },
    []
  );

  const createOffer = useCallback(
    async (peerId: string) => {
      const pc = ensurePeer(peerId);
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      sendSignal(peerId, { type: 'offer', sdp: pc.localDescription ?? offer });
    },
    [ensurePeer, sendSignal]
  );

  // A call whose notification was tapped but hasn't rung here yet (issue #21).
  const expectedCallRef = useRef<string | null>(null);
  // Calls answered or declined on this device: never "missed" (issue #21).
  const handledCallIdsRef = useRef<Set<string>>(new Set());

  const handleInvite = useCallback(
    (payload: CallInvitePayload) => {
      if (expectedCallRef.current === payload.callId) {
        expectedCallRef.current = null;
      }
      // The server rings again when we reconnect (issue #21): same call, so
      // keep ringing rather than declining it as a second call.
      if (sessionRef.current?.callId === payload.callId) {
        if (sessionRef.current.direction === 'incoming') {
          setState((prev) =>
            prev.status === 'ringing'
              ? { ...prev, participants: payload.participants }
              : prev
          );
          // In case the first word was lost with the old socket (D278).
          sendMessage(WS_EVENTS.CALL_RINGING, { callId: payload.callId });
        }
        return;
      }
      // Already busy — politely decline the second call.
      if (sessionRef.current) {
        sendMessage(WS_EVENTS.CALL_REJECT, { callId: payload.callId });
        return;
      }

      sessionRef.current = {
        callId: payload.callId,
        conversationId: payload.conversationId,
        type: payload.type,
        direction: 'incoming',
        initiator: payload.initiator,
      };
      setError(null);
      setNotice(null);
      setState({
        status: 'ringing',
        callId: payload.callId,
        conversationId: payload.conversationId,
        type: payload.type,
        direction: 'incoming',
        initiator: payload.initiator,
        participants: payload.participants,
        localStream: null,
        remoteStreams: {},
        micEnabled: true,
        cameraEnabled: payload.type === 'video',
        remoteMedia: {},
        reconnecting: [],
        facingMode: 'user',
        canFlipCamera: false,
        videoFallback: null,
        ringing: [],
      });

      // The caller's "Calling…" becomes "Ringing…" (D278).
      sendMessage(WS_EVENTS.CALL_RINGING, { callId: payload.callId });
      if (typeof navigator.vibrate === 'function') {
        navigator.vibrate([200, 100, 200]);
      }
      // Fetch the STUN/TURN servers while it rings, so answering is quick.
      void loadConfig();
    },
    [loadConfig, sendMessage]
  );

  const handleAccept = useCallback(
    (payload: CallAcceptPayload) => {
      const session = sessionRef.current;
      if (!session || session.callId !== payload.callId) return;
      const selfId = currentUserRef.current?.id;

      const media = { ...(payload.media ?? {}) };
      if (selfId) delete media[selfId];

      if (payload.userId === selfId) {
        session.confirmed = true;
        // We are the accepter: negotiate with everyone already in the call.
        // Any old connections (a rejoin) are rebuilt from scratch.
        for (const peerId of payload.participants) {
          if (peerId !== selfId) closePeer(peerId);
        }
        setState((prev) => ({
          ...prev,
          participants: payload.participants,
          status: prev.status === 'active' ? 'active' : 'connecting',
          remoteMedia: { ...prev.remoteMedia, ...media },
        }));
        announceMedia(
          mediaRef.current.micEnabled,
          mediaRef.current.cameraEnabled
        );
        for (const peerId of payload.participants) {
          if (peerId !== selfId) void createOffer(peerId);
        }
      } else {
        // Someone (re)joined: their offer will arrive, so drop our old connection.
        closePeer(payload.userId);
        setState((prev) => ({
          ...prev,
          participants: payload.participants,
          remoteMedia: { ...prev.remoteMedia, ...media },
          reconnecting: withoutId(prev.reconnecting, payload.userId),
        }));
      }
    },
    [announceMedia, closePeer, createOffer]
  );

  const handleMedia = useCallback((payload: CallMediaPayload) => {
    if (sessionRef.current?.callId !== payload.callId) return;
    setState((prev) => ({
      ...prev,
      remoteMedia: {
        ...prev.remoteMedia,
        [payload.userId]: {
          micEnabled: payload.micEnabled,
          cameraEnabled: payload.cameraEnabled,
        },
      },
    }));
  }, []);

  // Someone's phone is ringing for our call (D278).
  const handleRinging = useCallback((payload: CallRingingPayload) => {
    if (sessionRef.current?.callId !== payload.callId) return;
    setState((prev) =>
      prev.ringing.includes(payload.userId)
        ? prev
        : { ...prev, ringing: [...prev.ringing, payload.userId] }
    );
  }, []);

  const handleParticipant = useCallback((payload: CallParticipantPayload) => {
    if (sessionRef.current?.callId !== payload.callId) return;
    setState((prev) =>
      prev.reconnecting.includes(payload.userId)
        ? prev
        : { ...prev, reconnecting: [...prev.reconnecting, payload.userId] }
    );
  }, []);

  const handleReject = useCallback((payload: CallRejectPayload) => {
    const session = sessionRef.current;
    if (!session || session.callId !== payload.callId) return;
    if (payload.userId === currentUserRef.current?.id) return;
    setState((prev) => ({
      ...prev,
      participants: prev.participants.filter((id) => id !== payload.userId),
    }));
  }, []);

  const handleEnd = useCallback(
    (payload: CallEndPayload) => {
      const session = sessionRef.current;
      if (!session || session.callId !== payload.callId) return;

      const selfId = currentUserRef.current?.id;

      // One peer dropped; tear down just that peer connection.
      if (
        payload.reason === 'left' &&
        payload.userId &&
        payload.userId !== selfId
      ) {
        const pc = peersRef.current.get(payload.userId);
        if (pc) {
          pc.close();
          peersRef.current.delete(payload.userId);
        }
        setState((prev) => {
          const remoteStreams = { ...prev.remoteStreams };
          delete remoteStreams[payload.userId as string];
          const remoteMedia = { ...prev.remoteMedia };
          delete remoteMedia[payload.userId as string];
          return {
            ...prev,
            remoteStreams,
            remoteMedia,
            reconnecting: withoutId(
              prev.reconnecting,
              payload.userId as string
            ),
            participants: prev.participants.filter(
              (id) => id !== payload.userId
            ),
          };
        });
        if (
          aloneAfterLeaving(
            localStreamRef.current !== null,
            peersRef.current.size
          )
        ) {
          teardown(null);
        }
        return;
      }

      const message =
        payload.reason === 'no-answer'
          ? t('chat.call.noAnswer')
          : payload.reason === 'rejected'
            ? t('chat.call.declined')
            : payload.reason === 'busy'
              ? t('chat.callErrors.busy')
              : null;
      teardown(message);
    },
    [teardown]
  );

  const handleSignal = useCallback(
    async (payload: CallSignalPayload) => {
      const session = sessionRef.current;
      if (!session || session.callId !== payload.callId) return;
      const signal = payload.signal as CallSignal;
      if (!signal || typeof signal !== 'object') return;

      if (signal.type === 'offer') {
        const pc = ensurePeer(payload.fromUserId);
        await pc.setRemoteDescription(signal.sdp);
        await flushCandidates(payload.fromUserId, pc);
        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);
        sendSignal(payload.fromUserId, {
          type: 'answer',
          sdp: pc.localDescription ?? answer,
        });
        return;
      }

      if (signal.type === 'answer') {
        const pc = peersRef.current.get(payload.fromUserId);
        if (!pc) return;
        await pc.setRemoteDescription(signal.sdp);
        await flushCandidates(payload.fromUserId, pc);
        return;
      }

      if (signal.type === 'candidate') {
        const pc = peersRef.current.get(payload.fromUserId);
        if (pc && pc.remoteDescription) {
          await pc.addIceCandidate(signal.candidate).catch(() => undefined);
        } else {
          const pending =
            pendingCandidatesRef.current.get(payload.fromUserId) ?? [];
          pending.push(signal.candidate);
          pendingCandidatesRef.current.set(payload.fromUserId, pending);
        }
      }
    },
    [ensurePeer, flushCandidates, sendSignal]
  );

  const handleError = useCallback(
    (payload: CallErrorPayload) => {
      const session = sessionRef.current;
      if (session && payload.callId && session.callId !== payload.callId)
        return;
      teardown(payload.message);
      setError(payload.message);
    },
    [teardown]
  );

  // Signed in again: the server lists the calls still ringing for us, then
  // rings them again. Handled here, in arrival order, so the re-ring that
  // follows finds the session it belongs to (issue #21).
  const handleAuthSuccess = useCallback(
    (payload: AuthSuccessPayload) => {
      const session = sessionRef.current;
      const { action, missed } = afterReconnect({
        session: session && {
          callId: session.callId,
          direction: session.direction,
          answered: localStreamRef.current !== null,
          confirmed: session.confirmed === true,
        },
        expectedCallId: expectedCallRef.current,
        handledCallIds: handledCallIdsRef.current,
        ringingCallIds: payload?.ringingCallIds,
      });
      expectedCallRef.current = null;
      if (session && action === 'rejoin') {
        // The server keeps accepted participants for a short grace period.
        sendMessage(WS_EVENTS.CALL_REJOIN, { callId: session.callId });
      } else if (session && action === 'accept') {
        // Answered while the socket was down: that accept never left.
        sendMessage(WS_EVENTS.CALL_ACCEPT, { callId: session.callId });
      } else if (action === 'drop') {
        teardown(
          missed ? t('chat.call.missedNotice') : t('chat.callErrors.ended')
        );
        return;
      }
      if (missed) setNotice(t('chat.call.missedNotice'));
    },
    [sendMessage, teardown]
  );

  useEffect(() => {
    const off = onEvent((message) => {
      switch (message.event) {
        case 'auth:success':
          handleAuthSuccess(message.payload as AuthSuccessPayload);
          break;
        case WS_EVENTS.CALL_INITIATE:
          handleInvite(message.payload as CallInvitePayload);
          break;
        case WS_EVENTS.CALL_ACCEPT:
          handleAccept(message.payload as CallAcceptPayload);
          break;
        case WS_EVENTS.CALL_REJECT:
          handleReject(message.payload as CallRejectPayload);
          break;
        case WS_EVENTS.CALL_END:
          handleEnd(message.payload as CallEndPayload);
          break;
        case WS_EVENTS.CALL_SIGNAL:
          void handleSignal(message.payload as CallSignalPayload);
          break;
        case WS_EVENTS.CALL_ERROR:
          handleError(message.payload as CallErrorPayload);
          break;
        case WS_EVENTS.CALL_MEDIA:
          handleMedia(message.payload as CallMediaPayload);
          break;
        case WS_EVENTS.CALL_PARTICIPANT:
          handleParticipant(message.payload as CallParticipantPayload);
          break;
        case WS_EVENTS.CALL_RINGING:
          handleRinging(message.payload as CallRingingPayload);
          break;
        default:
          break;
      }
    });
    return off;
  }, [
    onEvent,
    handleAuthSuccess,
    handleInvite,
    handleAccept,
    handleReject,
    handleEnd,
    handleSignal,
    handleError,
    handleMedia,
    handleParticipant,
    handleRinging,
  ]);

  // Release media if the provider unmounts mid-call.
  useEffect(() => () => teardown(), [teardown]);

  // Opened from an incoming-call notification: `?call=<id>` (issue #21). The
  // ring itself arrives over the socket; the id only tells us, once it has
  // signed in, whether to say the call was missed. It comes straight out of
  // the address so Back and reloads don't trip over it.
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    const { callId, search } = withoutCallParam(location.search);
    if (!callId) return;
    navigate(
      { pathname: location.pathname, search, hash: location.hash },
      { replace: true, state: location.state }
    );
    if (sessionRef.current?.callId === callId) return;
    expectedCallRef.current = callId;
    // A socket that is already signed in sends no new list; don't hold on.
    // (Not an effect cleanup: the navigate above re-runs this effect.)
    setTimeout(() => {
      if (expectedCallRef.current === callId) expectedCallRef.current = null;
    }, 15_000);
  }, [location, navigate]);

  const startCall = useCallback(
    async (conversationId: string, type: CallType) => {
      if (sessionRef.current) return;
      setNotice(null);
      setError(null);

      const config = await loadConfig();
      if (!config?.enabled) {
        setError(t('chat.callErrors.unavailable'));
        return;
      }

      const me = currentUserRef.current;
      const initiator: CallParticipant = {
        id: me?.id ?? '',
        handle: me?.handle ?? '',
        displayName: me?.displayName ?? 'Someone',
        avatarUrl: me?.avatarUrl ?? null,
      };
      const callId = crypto.randomUUID();

      sessionRef.current = {
        callId,
        conversationId,
        type,
        direction: 'outgoing',
        initiator,
      };

      try {
        const stream = await acquireMedia(type);
        localStreamRef.current = stream;
        setState({
          status: 'ringing',
          callId,
          conversationId,
          type,
          direction: 'outgoing',
          initiator,
          participants: initiator.id ? [initiator.id] : [],
          localStream: stream,
          remoteStreams: {},
          micEnabled: true,
          cameraEnabled: type === 'video',
          remoteMedia: {},
          reconnecting: [],
          facingMode: 'user',
          canFlipCamera: false,
          videoFallback: null,
          ringing: [],
        });
        sendMessage(WS_EVENTS.CALL_INITIATE, { callId, conversationId, type });
        announceMedia(true, type === 'video');
        if (type === 'video') {
          void hasSeveralCameras().then((several) =>
            setState((prev) => ({ ...prev, canFlipCamera: several }))
          );
        }
      } catch (err) {
        countClientFailure('call_setup_failed');
        teardown(mediaErrorMessage(err));
      }
    },
    [announceMedia, loadConfig, sendMessage, teardown]
  );

  const acceptCall = useCallback(async () => {
    const session = sessionRef.current;
    if (!session || session.direction !== 'incoming') return;
    handledCallIdsRef.current.add(session.callId);
    setError(null);

    try {
      // The person answering needs the STUN/TURN servers too: without them
      // a call only connects on the same network (2026-10-23). Loaded when
      // the ring arrived; this waits for it if that hasn't finished.
      await loadConfig();
      const stream = await acquireMedia(session.type);
      localStreamRef.current = stream;
      setState((prev) => ({
        ...prev,
        localStream: stream,
        status: 'connecting',
        micEnabled: true,
        cameraEnabled: session.type === 'video',
      }));
      sendMessage(WS_EVENTS.CALL_ACCEPT, { callId: session.callId });
      if (session.type === 'video') {
        void hasSeveralCameras().then((several) =>
          setState((prev) => ({ ...prev, canFlipCamera: several }))
        );
      }
    } catch (err) {
      countClientFailure('call_setup_failed');
      sendMessage(WS_EVENTS.CALL_REJECT, { callId: session.callId });
      teardown(mediaErrorMessage(err));
    }
  }, [loadConfig, sendMessage, teardown]);

  const rejectCall = useCallback(() => {
    const session = sessionRef.current;
    if (!session) return;
    handledCallIdsRef.current.add(session.callId);
    sendMessage(WS_EVENTS.CALL_REJECT, { callId: session.callId });
    teardown();
  }, [sendMessage, teardown]);

  const endCall = useCallback(() => {
    const session = sessionRef.current;
    if (!session) return;
    sendMessage(WS_EVENTS.CALL_END, { callId: session.callId });
    teardown();
  }, [sendMessage, teardown]);

  const toggleMic = useCallback(() => {
    const stream = localStreamRef.current;
    if (!stream) return;
    const next = !state.micEnabled;
    stream.getAudioTracks().forEach((track) => {
      track.enabled = next;
    });
    setState((prev) => ({ ...prev, micEnabled: next }));
    announceMedia(next, state.cameraEnabled);
  }, [announceMedia, state.micEnabled, state.cameraEnabled]);

  // The camera switch, for the button and for D225's automatic pause alike,
  // so others see "Camera off" either way (D36). Audio is left alone.
  const setCamera = useCallback(
    (next: boolean) => {
      const stream = localStreamRef.current;
      if (!stream) return;
      stream.getVideoTracks().forEach((track) => {
        track.enabled = next;
      });
      mediaRef.current = { ...mediaRef.current, cameraEnabled: next };
      setState((prev) => ({ ...prev, cameraEnabled: next }));
      announceMedia(mediaRef.current.micEnabled, next);
    },
    [announceMedia]
  );

  const clearSamples = useCallback(() => {
    for (const pc of peersRef.current.values()) {
      const watch = watchByPc.get(pc);
      if (watch) watch.samples = [];
    }
  }, []);

  // The person decided about video themselves: no more automatic pauses,
  // suggestions or lowered caps for this call (D225).
  const handOff = useCallback(() => {
    fallbackRef.current = { mode: 'overridden', lowered: {} };
    applyBitrateCaps();
    setState((prev) =>
      prev.videoFallback === null ? prev : { ...prev, videoFallback: null }
    );
  }, [applyBitrateCaps]);

  const toggleCamera = useCallback(() => {
    const next = !mediaRef.current.cameraEnabled;
    const { mode } = fallbackRef.current;
    if (next && (mode === 'paused' || mode === 'better')) handOff();
    // A fresh run of samples for whatever the camera does next.
    clearSamples();
    setCamera(next);
  }, [clearSamples, handOff, setCamera]);

  const resumeVideo = useCallback(() => {
    handOff();
    setCamera(true);
  }, [handOff, setCamera]);

  const dismissVideoHint = useCallback(() => handOff(), [handOff]);

  // D226: re-cap every sender when the call crosses the group size.
  const peopleInCall = state.participants.length;
  useEffect(() => {
    if (peopleInCall > 0) applyBitrateCaps();
  }, [peopleInCall, applyBitrateCaps]);

  // D225: read each outgoing video copy's stats every 2 s and let callRules
  // decide. Chained timeouts, so a slow getStats can't overlap the next read.
  const watchVideo = state.status === 'active' && state.type === 'video';
  useEffect(() => {
    if (!watchVideo) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const sampleOnce = async () => {
      const { mode } = fallbackRef.current;
      if (mode === 'overridden') return;
      // Camera off by choice: nothing to watch until it's back on.
      if (mode === 'watching' && !mediaRef.current.cameraEnabled) {
        clearSamples();
        return;
      }
      const connections: Record<string, VideoConnectionHistory> = {};
      for (const [peerId, pc] of [...peersRef.current]) {
        const watch = watchByPc.get(pc);
        const sender = videoSender(pc);
        if (
          !watch ||
          watch.connectedAt === null ||
          !sender ||
          pc.connectionState !== 'connected'
        ) {
          continue;
        }
        try {
          const report = await sender.getStats();
          const reading = readVideoSenderStats(
            report.values() as Iterable<RtcStat>,
            watch.previous
          );
          if (!reading) continue;
          const at = Date.now();
          watch.previous = {
            bytesSent: reading.bytesSent,
            timestamp: reading.timestamp,
          };
          watch.samples = [...watch.samples, { ...reading.sample, at }].filter(
            (sample) => at - sample.at <= VIDEO_SAMPLE_WINDOW_MS
          );
          connections[peerId] = {
            connectedAt: watch.connectedAt,
            cap: senderCap(peerId),
            samples: watch.samples,
          };
        } catch {
          // A connection closing mid-read; the others still count.
        }
      }
      if (cancelled) return;
      const decision = videoFallbackDecision(connections, fallbackRef.current);
      fallbackRef.current = decision.state;
      if (decision.action === 'pause') setCamera(false);
      // Also retries any cap the browser refused last time.
      applyBitrateCaps();
      const banner = fallbackBanner(decision.state.mode);
      setState((prev) =>
        prev.videoFallback === banner
          ? prev
          : { ...prev, videoFallback: banner }
      );
    };

    const tick = async () => {
      await sampleOnce();
      if (!cancelled)
        timer = setTimeout(() => void tick(), VIDEO_STATS_INTERVAL_MS);
    };
    timer = setTimeout(() => void tick(), VIDEO_STATS_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [watchVideo, applyBitrateCaps, clearSamples, senderCap, setCamera]);

  const ringMore = useCallback(
    (userIds: string[]) => {
      const session = sessionRef.current;
      if (!session || userIds.length === 0) return;
      sendMessage(WS_EVENTS.CALL_RING, { callId: session.callId, userIds });
    },
    [sendMessage]
  );

  // Swap front and back cameras by replacing the outgoing track on every
  // connection; nothing is renegotiated (D39).
  const flipCamera = useCallback(async () => {
    const stream = localStreamRef.current;
    const current = stream?.getVideoTracks()[0];
    if (!stream || !current) return;
    const facingMode = state.facingMode === 'user' ? 'environment' : 'user';
    try {
      const fresh = await navigator.mediaDevices.getUserMedia({
        video: videoConstraints({ exact: facingMode }),
      });
      const track = fresh.getVideoTracks()[0];
      if (!track) return;
      // The call ended, or flipped again, while the camera opened.
      if (localStreamRef.current !== stream) {
        track.stop();
        return;
      }
      // Whatever the camera is *now*: it may have been turned off (by hand or
      // by D225) while we waited, and others are told "camera off" (D36).
      track.enabled = mediaRef.current.cameraEnabled;
      // Into the stream before the awaits below, so a toggle meanwhile
      // reaches the new track too.
      stream.removeTrack(current);
      stream.addTrack(track);
      // A new MediaStream object so the self view re-renders.
      const next = new MediaStream(stream.getTracks());
      localStreamRef.current = next;
      setState((prev) => ({ ...prev, localStream: next, facingMode }));
      try {
        for (const pc of peersRef.current.values()) {
          await videoSender(pc)?.replaceTrack(track);
        }
      } finally {
        current.stop();
      }
    } catch {
      setError(t('chat.callErrors.couldNotSwitch'));
    }
  }, [state.facingMode]);

  const dismissNotice = useCallback(() => setNotice(null), []);

  const value: CallApi = {
    state,
    notice,
    error,
    startCall,
    acceptCall,
    rejectCall,
    endCall,
    toggleMic,
    toggleCamera,
    ringMore,
    flipCamera,
    resumeVideo,
    dismissVideoHint,
    dismissNotice,
  };

  return <CallContext.Provider value={value}>{children}</CallContext.Provider>;
}

export function CallProvider({ children }: { children: ReactNode }) {
  return <CallProviderInner>{children}</CallProviderInner>;
}

export function useCall(): CallApi {
  const context = useContext(CallContext);
  if (!context) {
    throw new Error('useCall must be used inside CallProvider');
  }
  return context;
}
